import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from '../../i18n';
import { Certification } from '../../types/api';
import { formatDateForDisplay, isDateExpired } from '../../utils/dateUtils';
import { AddCertificationDialog } from './AddCertificationDialog';

interface CertificationsSectionProps {
  certifications: Certification[];
  canEdit: boolean;
  saving: boolean;
  onRemove: (index: number) => void;
  // Add-certification dialog state (owned by the parent page).
  dialogOpen: boolean;
  newCertName: string;
  newCertExpiry: string;
  newCertDocument: File | null;
  onOpenDialog: () => void;
  onCloseDialog: () => void;
  onNameChange: (name: string) => void;
  onExpiryChange: (expiry: string) => void;
  onDocumentChange: (document: File | null) => void;
  onAdd: () => void;
}

function isCertificationExpired(cert: Certification): boolean {
  if (!cert.expiry_date) return false;
  return isDateExpired(cert.expiry_date);
}

/**
 * Certifications list + "Add certification" affordance for the User Profile
 * page (STD-014 split out of UserProfilePage.tsx). Owns no state — the add
 * dialog's form state stays in the parent so its success-path reset is
 * unchanged.
 */
export const CertificationsSection: React.FC<CertificationsSectionProps> = ({
  certifications,
  canEdit,
  saving,
  onRemove,
  dialogOpen,
  newCertName,
  newCertExpiry,
  newCertDocument,
  onOpenDialog,
  onCloseDialog,
  onNameChange,
  onExpiryChange,
  onDocumentChange,
  onAdd,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('profile.certifications')}</Typography>
          {canEdit && (
            <Button size="small" startIcon={<AddIcon />} onClick={onOpenDialog}>
              {t('profile.addCertification')}
            </Button>
          )}
        </Box>

        {certifications.length === 0 ? (
          <Typography color="text.secondary">{t('profile.noCertifications')}</Typography>
        ) : (
          <List disablePadding>
            {certifications.map((cert, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <ListItem disableGutters>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{cert.name}</Typography>
                        {isCertificationExpired(cert) && (
                          <Chip label={t('profile.expired')} size="small" color="error" />
                        )}
                      </Box>
                    }
                    secondary={
                      cert.expiry_date
                        ? `${t('profile.expiresOn')}: ${formatDateForDisplay(cert.expiry_date)}`
                        : undefined
                    }
                  />
                  <ListItemSecondaryAction>
                    {cert.document_url && (
                      <IconButton
                        size="small"
                        href={cert.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mr: 1 }}
                      >
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canEdit && (
                      <IconButton size="small" color="error" onClick={() => onRemove(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <AddCertificationDialog
        open={dialogOpen}
        saving={saving}
        name={newCertName}
        expiry={newCertExpiry}
        document={newCertDocument}
        onNameChange={onNameChange}
        onExpiryChange={onExpiryChange}
        onDocumentChange={onDocumentChange}
        onClose={onCloseDialog}
        onAdd={onAdd}
      />
    </>
  );
};
